import { useMemo } from 'react'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  selectExpectedAnnualTaxFormNeeds,
  selectLateFilingStatusType,
  selectShowLateFilingAlert,
  LateFilingStatus,
  selectRelevantLateFilingDeadline,
} from '../annualTaxFilings.selector'
import { Alert, GridRowColumn, Link } from '@/componentsV1/BaseComponents'
import { TaxFormType } from '@/features/taxes/annual-taxes/questionnaires/constants'

const SCORP_MORE_INFO_URL =
  'https://support.joinheard.com/hc/en-us/articles/29760139682199'
const PERSONAL_MORE_INFO_URL =
  'https://support.joinheard.com/hc/en-us/articles/23239712683031'

export const LateFilingAlert = () => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const formNeeds = useReselector(selectExpectedAnnualTaxFormNeeds, taxYear)
  const lateFilingStatusType = useReselector(selectLateFilingStatusType)

  const deadline = useReselector(selectRelevantLateFilingDeadline)

  const showAlert = useReselector(selectShowLateFilingAlert)

  const headerText = `${taxYear} Tax Deadline: ${deadline?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}`

  const isScorp = formNeeds.includes(TaxFormType.form1120s)
  const linkHref = isScorp ? SCORP_MORE_INFO_URL : PERSONAL_MORE_INFO_URL

  const bodyText = useMemo(() => {
    switch (lateFilingStatusType.status) {
      case LateFilingStatus.taxChecklistNeeded:
        return (
          <>
            After this date, we may not be able to file ahead of your extended
            deadline. Your return could be filed late and you may owe penalties
            and interest.{' '}
            <Link href={linkHref} newPage size="small">
              More info
            </Link>
          </>
        )
      case LateFilingStatus.tskMissed:
      case LateFilingStatus.taxChecklistMissed:
      case LateFilingStatus.notRelevant:
        return null
      default:
        return lateFilingStatusType.status satisfies never
    }
  }, [lateFilingStatusType.status, linkHref])

  if (
    lateFilingStatusType.status === LateFilingStatus.notRelevant ||
    !showAlert ||
    !bodyText
  ) {
    return null
  }

  return (
    <GridRowColumn>
      <Alert title={headerText} type="warn">
        {bodyText}
      </Alert>
    </GridRowColumn>
  )
}
